import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ObjectUtils } from "abweb/utils/ObjectUtils";

// Any class that has a prototype member of the correct type (works for abstract or concrete classes!)

// Only works for non-abstract classes (concrete), as those have constructors and abstract classes don't!
// declare type ConcreteClassType<ClassT> = /* Function & */ new (...args: any[]) => ClassT;

export class ClassUtils {
  constructor() {
    // static class, can't create an instance
  }

  /* If the thing arg is null, null is returned. Otherwise, if thing is a primitive type (string|number|boolean|undefined|symbol|bigint),
   * the 'typeof' value will be returned. If it isn't a primitive, return the constructor (aka class) function if found,
   * otherwise it is a pure function so simply return the pure function as the function itself is the 'type'.
   */

  // Sigh - I which Javascript and/or Typescript had true support for the 'class' type (including primitives)
  static getClassOrPrimitive(thing) {
    // typeof null returns "object"
    if (typeof thing === "object") {
      if (thing === null) {
        return thing;
      } else {
        return thing.constructor;
      }
    } else {
      if (typeof thing === "function") {
        return thing;
      } else {
        // No catch case needed for odd typeof values, as those are from old IE:
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/typeof
        return typeof thing;
      }
    }
  }

  // Note that as thing is an actual object, it must be a concrete class!
  static getClass(thing) {
    // Note Object interface has 'constructor' member so just return it
    return thing == null ? null : thing.constructor;
  }

  /**
   * Gets the class name for the argument.
   *
   * @param thing Can be a type (class) or an instance of something.  Note that historically all supported browser
   *   JavaScript implementations were consistent in that they could _not_ resolve class names for external (non-ts)
   *   like the '$' alias for JQuery.  Chrome has become more intelligent about this so can properly resolve it so
   *   care should be taken when using this function to determine the class name for non-ts objects/types.
   *
   * @returns {any} Class name or possibly primitive type.
   */
  static getClassname(thing) {
    const thingType = ClassUtils.getClassOrPrimitive(thing);
    // Return "null" if thingType is "undefined" for backward compatibility.
    if (thingType === null || thingType === "undefined") {
      return "Null"; // special case
    }
    if (typeof thingType === "string") {
      return thingType; // It is a primitive so was returned as a string (number|string|boolean|symbol|bigint)
    }
    /*
      displayName is a React concept used to provide meaningful names to HOC:
       The displayName string is used in debugging messages. Usually, you don't need to set it explicitly
      because it's inferred from the name of the function or class that defines the component. You might
      want to set it explicitly if you want to display a different name for debugging purposes or when you
      create a higher-order component...
       https://reactjs.org/docs/react-component.html#displayname
       If thing has a displayName, ensure that the thing is also a react component before using this
      value.
     */
    let anyThingType = thingType;
    if (anyThingType.displayName && (anyThingType.render || anyThingType.prototype && anyThingType.prototype.render)) {
      return anyThingType.displayName;
    }
    // Either a named function, or a constructor,
    // which is another type of named function where the name happens to be the 'classname'
    if (anyThingType.name) {
      return anyThingType.name;
    }

    // Raw js function.  Either it is named or anonymous function (so return parsed name or simply 'Function')
    const funcNameRegex = /function ([^(]{1,})\(/;
    const results = funcNameRegex.exec(thingType.toString());
    return results && results.length > 1 ? results[1] : "anonymous function";
  }

  /**
   * Gets the base (super) type given a class type.
   * @param type Class type.
   * @return Base (super) class type.
   * Will return null (not Object) if type has no base class.
   */
  static getBaseClass(type) {
    if (type == null) {
      return null;
    }
    const t = type;
    if (t.prototype.__proto__ == null) {
      return null;
    } else {
      return t.prototype.__proto__.constructor;
    }
  }

  /**
   * Tests if type is of type targetType or derived from targetType.
   * @param clazz Type (class) to test.
   * @param targetClazz Target type (class).
   * @returns {any} true if type is of the same type as targetType or derived from targetType,
   * or false if not.
   */
  static isInProtoChain(clazz, targetClazz) {
    if (clazz === targetClazz) {
      return true;
    }
    return ClassUtils.isInProtoClassname(clazz, ClassUtils.getClassname(targetClazz));
  }
  static isInProtoClassname(clazz, targetName) {
    // Walk up proto chain
    let p = clazz.prototype;
    while (p != null) {
      if (ClassUtils.getClassname(p.constructor) === targetName) {
        return true;
      }
      p = p.__proto__;
    }
    return false;
  }

  /**
   * An iframe-safe version of 'instanceof' - see http://jira:8080/browse/CAT-621
   * @param obj
   * @param {ClassType<ClassT>} targetClazz
   * @return {obj is ClassT}
   *
   * <p>Note due to limitations of typeguard (which use structural typing), it is not quite as strong as
   * 'instanceof' which uses nominal typing (see https://github.com/Microsoft/TypeScript/issues/7271).  99% of the time,
   * isInstanceOf behaves exactly the same as the builtin instanceof operator.  The 1% case happens when you have
   * multiple classes with the exact same members (such as class A1 extends A {}), as the 'else' branch
   * of an isInstanceOf(obj, A1) check will then incorrectly narrow to the 'never' type as they are structurally equivalent,
   * resulting in "error TS2339: Property 'foo' does not exist on type 'never'." when you try to access obj.foo
   * in the else block (and 'foo' is a prop on the 'A' class).
   * If this happens, the <b>workaround</b> is to simply widen the type back to the base class:
   * <pre>
   *   let obj: A = ...
   *   if (isInstanceOf(obj, A1)) {
   *   ...
   *   }
   *   else {
   *      const wideObj: A;
   *      ...   wideObj.foo ...          // Otherwise get TS2339 if access obj.foo
   *
   *   }
   * </pre>
   * </p>
   */
  static isInstanceOf(obj, targetClazz) {
    const clazz = ObjectUtils.getClass(obj);
    if (clazz == null) {
      return false;
    }
    const x = ClassUtils.getClassOrPrimitive(obj);
    if (x === "string" || x === "number" || x === "boolean")
      // primitives don't have prototypes.
      {
        return x === ClassUtils.getClassname(targetClazz).toLowerCase();
      }

    // Kendo classes are ES5 based, so getClassname() always returns init for all of their classes.
    // We can't tell kendo.data.DataSource from kendo.data.ObservableObject.  So, I give up.  Back to 'instanceof'.
    // If clazz is one of ours that subclasses from kendo, for example DataSourceValue (via KendoObservableObjectProxy), we'll still use the back door.
    if (ClassUtils.getClassname(targetClazz) === "init" || ClassUtils.getClassname(targetClazz) === "anonymous function") {
      // eslint-disable-next-line abinitio/no-instanceof
      return obj instanceof targetClazz;
    }
    return ClassUtils.isInProtoChain(clazz, targetClazz);
  }

  /**
   * Helper function to throw an error if class does not extend from targetClass.
   * This is typically a programmer error, so log and throws an error
   * @param testClass
   * @param targetClass
   */
  static throwIfNotInProtoChain(testClass, targetClass) {
    if (!ClassUtils.isInProtoChain(testClass, targetClass)) {
      const errMsg = `Class '${ClassUtils.getClassname(testClass)}' does not extend class '${ClassUtils.getClassname(targetClass)}'.`;
      ClassUtils.LOG.error(errMsg);
      throw new Error(errMsg);
    }
  }

  /**
   * Return true if all items in the array are instances of class "clazz"
   * The "clazz" may be a concrete or abstract class.
   * The "clazz" must extend or implement BaseT, which is the declared or inferred type of each item in the array
   *
   * For example:
   * ```
   * let records: IRecord[] = [new Entity(), new Entity()]; // where Entity implements IRecord
   * if (areInstancesOf(records, Entity)
   * {
   *   // records are now inferred to be an array of Entity inside the block
   *   let id = records[0].entityId;
   * }
   * ```
   */
  static areInstancesOf(items, clazz) {
    return items.every(item => ClassUtils.isInstanceOf(item, clazz));
  }
}
// do NOT use AbWebServices.getAbWebLogger or get circular static dependency
_defineProperty(ClassUtils, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=ClassUtils.js.map
